"use client";

// ChunkRecovery — what happens after a deploy, to a tab that was already open.
//
// A page loads its code in pieces. A tab opened yesterday still points at
// yesterday's pieces, and once a new build lands they are gone: the next panel
// it opens fails to download, and React.lazy remembers that failure for the life
// of the page, so no retry, remount or "try again" can ever fix it. The only
// cure is a reload, which fetches the new build. Without this, that moment was a
// panel that never opened, or Next's own "this page couldn't load".
//
// Mounted ONCE, in the root layout. It hears about a failed download three ways
// (a runtime error that says so, a promise nobody caught, or a boundary that
// watched the same code fail twice) and then does the one safe thing:
//
//   · first failure in a minute → say "innernet just updated. one moment." for
//     a breath, and reload;
//   · a second failure inside that minute → the reload met the same trouble, so
//     DO NOT reload again (that is a loop). Say it plainly, with a button.
//
// The minute is kept in sessionStorage (innernet:chunk-reload); if storage is
// blocked we cannot promise there is no loop, so we never reload on our own.
// Offline is not a stale deploy: a download that failed with no network says
// nothing here, and the boundary that wanted it keeps its own button.

import { useEffect, useState } from "react";
import NettiError from "./brand/NettiError";
import { reportClientError } from "../lib/os/reportError";
import { CHUNK_EVENT, chunkVerdict, isChunkError } from "../lib/os/resilience";

/** how long "innernet just updated" is on screen before the reload */
const NOTE_MS = 700;

export default function ChunkRecovery() {
  const [view, setView] = useState<"updating" | "gave-up" | null>(null);

  useEffect(() => {
    let handled = false;

    const act = (err: unknown) => {
      if (handled) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      handled = true;
      let store: Storage | null = null;
      try {
        store = window.sessionStorage;
      } catch {
        /* blocked: chunkVerdict will give up rather than risk a loop */
      }
      const verdict = chunkVerdict(store, Date.now());
      reportClientError(err, "chunk", { verdict });
      if (verdict === "reload") {
        setView("updating");
        setTimeout(() => window.location.reload(), NOTE_MS);
      } else {
        setView("gave-up");
      }
    };

    // a runtime error that says a download failed. (Not a script or stylesheet
    // element that failed to load: that same failure also rejects the import()
    // that wanted it, which retryImport and the boundaries are already handling,
    // and acting on the element would reload a page that was about to recover.)
    const onError = (e: Event) => {
      const ev = e as ErrorEvent;
      if (isChunkError(ev.error) || isChunkError(ev.message)) act(ev.error ?? ev.message);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (isChunkError(e.reason)) act(e.reason);
    };
    const onSignal = (e: Event) => act((e as CustomEvent).detail);

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener(CHUNK_EVENT, onSignal);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener(CHUNK_EVENT, onSignal);
    };
  }, []);

  if (!view) return null;
  return view === "updating" ? (
    <NettiError variant="page" overlay state="updated" homeHref={null} />
  ) : (
    <NettiError variant="page" overlay state="failed" retryLabel="reload" onRetry={() => window.location.reload()} homeHref={null} />
  );
}
